/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: EslUserFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the ESL Users module.
----------------------------------------------------------------*/

import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Filter, Plus, Users, Search, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import { SecretInput } from '../../components/ui/SecretField';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { MultiSelectButton } from '../../components/ui/MultiSelectButton';
import { MultiSelectModal } from '../../components/ui/MultiSelectModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { EslUser, EslUserFormState } from './types';
import { FREE_SWITCH_EVENTS } from './eventOptions';
import { navigateToEslUserDuplicate } from './eslUsersActions';
import { fetchEslUser, createEslUser, updateEslUser, fetchAllEslUsers, deleteEslUser, CreateEslUserPayload, UpdateEslUserPayload } from './api';
import { EslUserGeneralSection } from './components/EslUserGeneralSection';

type AccessMode = 'inbound' | 'outbound' | 'embedded';


const DEFAULT_FORM_STATE: EslUserFormState = {
  username: '',
  password: '',
  accessMode: 'inbound',
  allowedIp: '0.0.0.0/0',
  listenIp: '::',
  listenPort: '8021',
  natMap: false,
  applyInboundAcl: '',
  stopOnBindError: false,
  enabled: true,
  description: '',
  eventFilters: [],
  createdAt: null,
  updatedAt: null
};

const parseSettingsForState = (settings: unknown, descriptionFallback = '') => {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return {
      description: descriptionFallback,
      eventFilters: [] as string[]
    };
  }

  const {
    description = descriptionFallback,
    eventFilters,
    event_filters
  } = settings as Record<string, unknown>;

  let parsedFilters: string[] = [];
  const rawFilters = eventFilters ?? event_filters;

  if (Array.isArray(rawFilters)) {
    parsedFilters = rawFilters.map((filter) => String(filter)).filter(Boolean);
  } else if (typeof rawFilters === 'string' && rawFilters.trim()) {
    parsedFilters = rawFilters
      .split(',')
      .map((filter) => filter.trim())
      .filter(Boolean);
  }

  return {
    description: typeof description === 'string' ? description : descriptionFallback,
    eventFilters: Array.from(new Set(parsedFilters))
  };
};

const mapResponseToFormState = (user: EslUser): EslUserFormState => {
  const { description, eventFilters } = parseSettingsForState(user.settings);

  return {
    username: user.username ?? '',
    password: '',
    accessMode: (user.accessMode ?? 'inbound') as AccessMode,
    allowedIp: user.allowedIp ?? '0.0.0.0/0',
    listenIp: user.listenIp ?? '::',
    listenPort: String(user.listenPort ?? 8021),
    natMap: Boolean(user.natMap),
    applyInboundAcl: user.applyInboundAcl ?? '',
    stopOnBindError: Boolean(user.stopOnBindError),
    enabled: Boolean(user.enabled),
    description,
    eventFilters: Array.from(
      new Set(
        Array.isArray(user.eventFilters)
          ? user.eventFilters
          : eventFilters
      )
    ),
    createdAt: user.createdAt ?? null,
    updatedAt: user.updatedAt ?? null
  };
};

interface EslUserListItem {
  id: number;
  username: string;
  accessMode: string;
  enabled: boolean;
}

export default function EslUserFormView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const { showError, showSuccess, showInfo } = useNotifications();

  const [formData, setFormData] = useState<EslUserFormState>(DEFAULT_FORM_STATE);
  const [originalData, setOriginalData] = useState<EslUserFormState>(DEFAULT_FORM_STATE);
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showEventFiltersModal, setShowEventFiltersModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDefaultUser, setIsDefaultUser] = useState(false);

  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Quick List state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListUsers, setQuickListUsers] = useState<EslUserListItem[]>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const eventFilterItems = useMemo(
    () => FREE_SWITCH_EVENTS.map((event) => ({ id: event, name: event })),
    []
  );

  const tRef = useRef(t);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [showError, showInfo]);

  // Load quick list
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const users = await fetchAllEslUsers();
      setQuickListUsers(users.map((u) => ({
        id: u.id,
        username: u.username || '',
        accessMode: u.accessMode || 'inbound',
        enabled: u.enabled ?? true,
      })));
    } catch (error) {
      const msg = error instanceof Error ? error.message : tRef.current('eslUsers.messages.loadError');
      showErrorRef.current(msg);
      setQuickListUsers([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListUsers.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, loadQuickList]);

  // Filter users client-side
  const filteredQuickList = useMemo(() => {
    if (!quickListSearch.trim()) {
      return quickListUsers;
    }
    const searchLower = quickListSearch.toLowerCase();
    return quickListUsers.filter(
      (user) =>
        user.username?.toLowerCase().includes(searchLower)
    );
  }, [quickListUsers, quickListSearch]);

  // Handle duplicate from form
  const handleDuplicateFromForm = useCallback(() => {
    if (isEditMode && id) {
      navigateToEslUserDuplicate(navigate, id);
    }
  }, [isEditMode, id, navigate]);

  const hasChanges = useMemo(() => JSON.stringify(formData) !== JSON.stringify(originalData), [formData, originalData]);

  useEffect(() => {
    if (isEditMode && id && !duplicateId) {
      return;
    }

    if (duplicateId) {
      // Will be handled by the fetch effect below
      return;
    }

    const defaultState = { ...DEFAULT_FORM_STATE };
    setFormData(defaultState);
    setOriginalData({ ...defaultState });
    setIsLoading(false);
  }, [id, isEditMode, duplicateId]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = searchParams.get('_reset');

    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      const defaultState = { ...DEFAULT_FORM_STATE };
      setFormData(defaultState);
      setOriginalData({ ...defaultState });

      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, searchParams]);

  useEffect(() => {
    const userIdToLoad = duplicateId || (isEditMode ? id : null);
    if (!userIdToLoad) {
      return;
    }

    let isActive = true;

    const loadEslUser = async () => {
      try {
        setIsLoading(true);
        const result = await fetchEslUser(userIdToLoad);

        if (!isActive) {
          return;
        }

        const mapped = mapResponseToFormState(result.data);

        if (duplicateId) {
          // Clear ID for duplicate, modify username
          mapped.username = `${mapped.username}_copy`;
          mapped.password = ''; // Clear password for duplicate
          showInfoRef.current(tRef.current('eslUsers.messages.duplicateHint'));
        }

        setFormData(mapped);
        setOriginalData(mapped);
        setIsDefaultUser(Boolean(result.data.isDefault));
      } catch (error) {
        if (!isActive) {
          return;
        }

        const currentT = tRef.current;
        const message = error instanceof Error ? error.message : currentT('eslUsers.messages.loadError');
        showErrorRef.current(message);
        navigate('/settings/pbx/esl-users', { replace: true });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadEslUser();

    return () => {
      isActive = false;
    };
  }, [id, isEditMode, duplicateId, navigate]);

  const handleFieldChange = useCallback(<K extends keyof EslUserFormState>(field: K, value: EslUserFormState[K]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleAccessModeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      accessMode: value as AccessMode
    }));
  }, []);

  const handleEventFiltersChange = useCallback((filters: string[]) => {
    const uniqueFilters = Array.from(new Set(filters));
    setFormData((prev) => ({
      ...prev,
      eventFilters: uniqueFilters
    }));
  }, []);

  const handleCancel = useCallback(() => {
    setFormData(originalData);
  }, [originalData]);

  const parseSettingsPayload = useCallback((): { settings: Record<string, unknown>; description: string } | null => {
    const description = formData.description.trim();

    return {
      settings: {},
      description
    };
  }, [formData.description]);

  const handleSubmit = useCallback(async (): Promise<EslUser | undefined> => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = t('common.required');
    }

    if (!isEditMode && !formData.password.trim()) {
      newErrors.password = t('common.required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showError(t('eslUsers.messages.validationError'));
      return undefined;
    }

    setErrors({});
    const parsed = parseSettingsPayload();
    const settingsPayload: Record<string, unknown> = { ...parsed.settings };

    if (formData.eventFilters.length > 0) {
      settingsPayload.eventFilters = Array.from(new Set(formData.eventFilters));
    }

    const trimmedPassword = formData.password.trim();
    const basePayload = {
      username: formData.username.trim(),
      accessMode: formData.accessMode,
      allowedIp: formData.allowedIp.trim() || '0.0.0.0/0',
      listenIp: formData.listenIp.trim() || '::',
      listenPort: Number(formData.listenPort) || 8021,
      natMap: formData.natMap,
      applyInboundAcl: formData.applyInboundAcl.trim() || null,
      stopOnBindError: formData.stopOnBindError,
      enabled: formData.enabled,
      settings: settingsPayload,
      description: parsed.description
    };

    setIsSaving(true);
    try {
      let result;
      if (isEditMode) {
        const updatePayload: UpdateEslUserPayload = {
          ...basePayload,
          ...(trimmedPassword ? { password: trimmedPassword } : {}),
        };
        result = await updateEslUser(id!, updatePayload);
      } else {
        const createPayload: CreateEslUserPayload = {
          ...basePayload,
          password: trimmedPassword || '',
        };
        result = await createEslUser(createPayload);
      }

      const mapped = mapResponseToFormState(result.data);
      setFormData(mapped);
      setOriginalData(mapped);
      showSuccess(t('eslUsers.messages.saveSuccess'));

      // Return saved data for useSaveWithAction
      if (result.data?.id) {
        return result.data;
      }
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : t('eslUsers.messages.saveError');
      showError(message);
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, id, isEditMode, parseSettingsPayload, showError, showInfo, showSuccess, t]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as EslUser;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/settings/pbx/esl-users',
    newPath: '/settings/pbx/esl-users/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // ESL Users use ID for navigation
      return `/settings/pbx/esl-users/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'eslUsers:saveAndNewData',
  });

  const handleDeleteConfirm = useCallback(async () => {
    if (!id || id === 'new') return;
    setIsDeleting(true);
    try {
      await deleteEslUser(Number(id));
      showSuccess(t('eslUsers.messages.deleteSuccess'));
      navigate('/settings/pbx/esl-users');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('eslUsers.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, navigate, showError, showSuccess, t]);

  return (
    <div className="p-6 pb-12 space-y-4">
      <ModuleFormHeader
        icon={<Users className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('eslUsers.form.editTitle')} - ${formData.username || ''}` : t('eslUsers.form.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate(-1)}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('eslUsers.actions.quickList', 'Quick List'),
            onClick: () => setShowQuickList(true)
          },
          ...(isEditMode && id
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: handleDuplicateFromForm
              }
            ]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('eslUsers.actions.add'),
            onClick: () => navigate('/settings/pbx/esl-users/new')
          }
        ]}
      />

      <div className="space-y-4">
        <EslUserGeneralSection
          formData={formData}
          isEditMode={isEditMode}
          onFieldChange={handleFieldChange}
          errors={errors}
          t={t}
        />

        <FormBox title={t('eslUsers.form.sections.metadata')} icon={<Users className="h-5 w-5" />}>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eventFilters" className="text-left">
                {t('eslUsers.form.fields.eventFilters.label')}
              </Label>
              <MultiSelectButton
                items={eventFilterItems}
                selectedIds={formData.eventFilters}
                onClick={() => setShowEventFiltersModal(true)}
                label={t('eslUsers.form.fields.eventFilters.label')}
                placeholder={t('eslUsers.form.fields.eventFilters.placeholder')}
                icon={Filter}
                hideLabel
                maxPreviewItems={25}
              />
            </div>

          </div>
        </FormBox>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode && !duplicateId ? () => setShowDeleteModal(true) : undefined}
        deleteDisabled={isDefaultUser || isDeleting}
        deleteTooltip={isDefaultUser ? t('eslUsers.messages.defaultDeleteDisabled', 'Cannot delete default profile') : undefined}
        isEditMode={isEditMode}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        saveDisabled={isSaving}
        isLoading={isSaving}
        useSaveDropdown={true}
        saveDropdownStorageKey="eslUsers:saveActionPreference"
        hasChanges={hasChanges}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('common.deleteItem')}
        message={t('eslUsers.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <MultiSelectModal
        isOpen={showEventFiltersModal}
        onClose={() => setShowEventFiltersModal(false)}
        title={t('eslUsers.form.fields.eventFilters.modalTitle')}
        description={t('eslUsers.form.fields.eventFilters.modalDescription')}
        items={eventFilterItems}
        selectedIds={formData.eventFilters}
        onSelectionChange={handleEventFiltersChange}
        icon={Filter}
        searchPlaceholder={t('eslUsers.form.fields.eventFilters.searchPlaceholder')}
        enableSelectAll
        selectAllLabel={t('common.selectAll')}
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('eslUsers.actions.quickList', 'ESL Users')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(user) => {
          setShowQuickList(false);
          navigate(`/settings/pbx/esl-users/${user.id}`);
        }}
        currentItemId={isEditMode ? id : null}
        renderItem={(user, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{user.username}</p>
              <p className="text-sm text-muted-foreground">
                {t(`eslUsers.accessModes.${user.accessMode}`, user.accessMode)}
              </p>
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('eslUsers.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('eslUsers.quickListSearch', 'Search by username')}
        emptyMessage={t('eslUsers.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('eslUsers.quickListNoResults', 'No users found')}
        headerIcon={<Users className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
